// TeamMembersSection — the team's ONE list of people (ADR-083 §2), the page's
// Members section body: each row the person's face (the avatar, the name, the
// project ROLE read from the membership, the account status when not active),
// whether the project's bot reaches them, and "can be assigned" — a switch for a
// manager (`team:manage` × team scope), a word for everyone else — then the
// remove door: a confirm; the server refuses `CHAINS_END` while the person holds
// one of the team's responsibilities (handed over first), read back in the
// dialog. Adding is the page's "Add members" drawer. The PEEK renders the same
// list `dense` — the avatar and the name, the exception marks alone (a disabled
// account, not assignable): no role line, no reach, no doors; a person acts from
// the peek, the page reads everything (ADR-036 §2). The name is the reference
// the server allows (`can_open` — PersonRef), quiet — a list of people, never a
// column of blue — its peek the HOST's (`peekPerson`: the page opens, the sheet
// follows).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamDetail, TeamMember } from '@ems-app/schemas/teams/responses';
import { PersonRef } from '../../components/patterns/PersonRef.js';
import { Avatar } from '../../components/ui/Avatar.js';
import { Badge } from '../../components/ui/Badge.js';
import { TelegramBadge } from '../../components/patterns/userDisplay.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { IconButton } from '../../components/ui/IconButton.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { icons } from '../../components/ui/icons.js';
import { useRemoveTeamMember, useUpdateTeamMember } from '../../hooks/useTeamMutations.js';
import { useQuickSave } from '../../components/patterns/forms/useQuickSave.js';

export const memberName = (m: Pick<TeamMember, 'first_name' | 'last_name' | 'username'>): string => `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.username;

export interface TeamMembersSectionProps {
  projectId: string;
  team: Pick<TeamDetail, 'id' | 'name' | 'members'>;
  /** The viewer runs the team and it is not disabled — the "can be assigned" switch shows. */
  canManage: boolean;
  /** The remove door shows — the viewer runs the team, disabled or not (a removal is the clean-up a switched-off team
   *  keeps open, ADR-080 §5); `disabled` says the removal ends what they answer for there. Defaults to `canManage`. */
  canRemove?: boolean;
  disabled?: boolean;
  /** The peek's face: the avatar and the name, the exception marks; no role line, no reach, no doors (whatever `canManage` says). */
  dense?: boolean;
  /** The host's peek for a member (a page opens it, a sheet follows to it); absent — the names stay plain. */
  peekPerson?: (userId: string) => void;
}

export function TeamMembersSection({ projectId, team, canManage, canRemove = canManage, disabled = false, dense = false, peekPerson }: TeamMembersSectionProps) {
  const { t } = useTranslation('teams');
  const { t: tu } = useTranslation('users');
  const update = useUpdateTeamMember(projectId);
  // The switch is a quick save (one value travels, no stamp): a refusal said once, the team re-read.
  const quickUpdate = useQuickSave(update, { key: 'team-member-switch' });
  const remove = useRemoveTeamMember(projectId);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const manages = canManage && !dense; // the dense face carries no doors
  const removes = canRemove && !dense;

  if (team.members.length === 0) return <p className="text-sm text-[var(--color-text-secondary)]">{t('detail.membersEmpty')}</p>;

  const statusWord = (m: TeamMember): string | null => (m.status === 'disabled' ? tu('status.disabled') : m.status === 'pending_activation' ? tu('lookup.pending') : null);

  return (
    <>
      <ul className={dense ? 'space-y-1' : 'divide-y divide-[var(--color-border)]'} aria-label={t('detail.membersList', { team: team.name })}>
        {team.members.map((m) => {
          const name = memberName(m);
          const state = statusWord(m);
          return (
            <li key={m.user_id} className={dense ? 'flex items-center gap-2 pointer-coarse:min-h-11' : 'flex flex-wrap items-center gap-x-3 gap-y-1 py-2'}>
              <Avatar name={name} size="sm" />
              <div className="min-w-0 flex-1">
                {/* The person as the reference they are (ADR-036 §6) — a press peeks them; plain text without the people key, or when the server's word refuses. */}
                <div className={`flex min-w-0 text-sm text-[var(--color-text-primary)] ${dense ? '' : 'font-medium'}`}>
                  <PersonRef projectId={projectId} id={m.user_id} name={name} canOpen={m.can_open} onPeek={peekPerson && (() => peekPerson(m.user_id))} quiet />
                </div>
                {!dense && (
                  <div className="truncate text-xs text-[var(--color-text-secondary)]">{[m.role_name ?? tu('lookup.admin'), <span key="u" dir="ltr">@{m.username}</span>].map((part, i) => (i === 0 ? part : [' · ', part]))}</div>
                )}
              </div>
              {state && <Badge variant="neutral" appearance="subtle">{state}</Badge>}
              {!dense && m.bot_reachable && <TelegramBadge state="linked" />}
              {manages ? (
                <Toggle
                  checked={m.can_be_assigned}
                  disabled={quickUpdate.busy}
                  onChange={(on) => void quickUpdate.save({ teamId: team.id, userId: m.user_id, body: { can_be_assigned: on } })}
                  label={t('detail.canBeAssigned')}
                  ariaLabel={t('detail.canBeAssignedOf', { name })}
                />
              ) : (
                // The EXCEPTION wears the mark; the default (assignable) wears nothing.
                !m.can_be_assigned && (
                  <Badge variant="neutral" appearance="subtle">
                    <icons.observer size={12} aria-hidden />
                    {t('detail.notAssignable')}
                  </Badge>
                )
              )}
              {removes && (
                <IconButton aria-label={t('detail.remove', { name })} variant="ghost" onClick={() => setRemoving(m)}>
                  <icons.close size={16} aria-hidden />
                </IconButton>
              )}
            </li>
          );
        })}
      </ul>
      {removes && (
      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('detail.removeTitle', { name: removing ? memberName(removing) : '', team: team.name })}
        description={t(disabled ? 'detail.removeBodyDisabled' : 'detail.removeBody')}
        confirmLabel={t('detail.removeConfirm')}
        onConfirm={async () => {
          if (!removing) return;
          // A refusal — CHAINS_END, the person still answers for this team: handed over first — is the dialog's to word.
          await remove.mutateAsync({ teamId: team.id, userId: removing.user_id });
        }}
      />
      )}
    </>
  );
}
